import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FallingItem, Basket, GameState, ScoreEffect } from './types';
import './App.css';

const PickGame: React.FC = () => {
  // Enhanced device detection for better responsive design
  const deviceInfo = useMemo(() => {
    if (typeof window === 'undefined') return { isMobile: false, isTablet: false, isDesktop: false, isLandscape: false };
    
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // Enhanced detection including screen size for Chrome dev tools
    const isMobileUA = /mobile|android|iphone|ipad|ipod|blackberry|windows phone/.test(userAgent);
    
    // Screen size based detection for Chrome dev tools
    const isMobileScreen = screenWidth <= 768 || screenHeight <= 768;
    const isTabletScreen = (screenWidth >= 768 && screenWidth < 1024) || 
                          (screenHeight >= 768 && screenHeight < 1024);
    
    // Combine UA and screen size detection
    const isMobile = isMobileUA || isMobileScreen;
    const isTablet = /tablet|ipad|playbook|silk/i.test(userAgent) || 
                     isTabletScreen || 
                     (screenWidth >= 768 && screenHeight >= 1024);
    const isDesktop = !isMobile && !isTablet;
    const isLandscape = screenWidth > screenHeight;
    
    // Force mobile detection for Chrome dev tools
    const isChromeDevTools = userAgent.includes('chrome') && (screenWidth <= 768 || screenHeight <= 768);
    const finalIsMobile = isMobile || isChromeDevTools;
    
    console.log('Device Detection:', {
      userAgent: userAgent.substring(0, 100),
      screenWidth,
      screenHeight,
      isMobileUA,
      isMobileScreen,
      isMobile,
      isTablet,
      isDesktop,
      isLandscape,
      isChromeDevTools,
      finalIsMobile
    });
    
    return { isMobile: finalIsMobile, isTablet, isDesktop, isLandscape };
  }, []);

  // Enhanced game state management
  const [gameState, setGameState] = useState<GameState>({
    score: 0,
    level: 1,
    timeLeft: 180, // 3 minutes
    gameStarted: false,
    gameOver: false,
    paused: false,
    highScore: 0,
    combo: 0,
    maxCombo: 0,
    itemsCaught: 0,
  });

  // Responsive canvas height calculation
  const canvasHeight = useMemo(() => {
    if (typeof window === 'undefined') return 600;
    
    if (deviceInfo.isTablet) {
      return deviceInfo.isLandscape ? window.innerHeight - 60 : window.innerHeight - 80;
    } else if (deviceInfo.isMobile) {
      return deviceInfo.isLandscape ? window.innerHeight - 40 : window.innerHeight - 100;
    } else {
      return window.innerHeight - 120;
    }
  }, [deviceInfo]);
  
  // Responsive basket sizing
  const basketConfig = useMemo(() => {
    const baseHeightVw = deviceInfo.isTablet ? 8 : deviceInfo.isMobile ? 6 : 7;
    const baseHeightPx = baseHeightVw * (window.innerWidth / 100);
    const basketHeightPercent = baseHeightPx / canvasHeight * 100;
    const maxBasketY = 100 - basketHeightPercent;
    
    return {
      heightPercent: basketHeightPercent,
      maxY: maxBasketY,
      speed: deviceInfo.isTablet ? 3.5 : deviceInfo.isMobile ? 2.8 : 2.5,
      widthPercent: deviceInfo.isMobile ? 15 : 10, // Wider on mobile for easier touch and visibility
    };
  }, [deviceInfo, canvasHeight]);

  // 1. PC端y坐标更靠下，判定盒宽高与图标一致
  const getBasketY = () => {
    if (deviceInfo.isDesktop) {
      // PC端，底部留2%空隙
      return 98 - basketConfig.heightPercent / 2;
    } else if (deviceInfo.isLandscape) {
      return basketConfig.maxY - 8;
    } else {
      return basketConfig.maxY;
    }
  };
  const [basket, setBasket] = useState<Basket>({
    x: 50,
    y: getBasketY(),
    speed: basketConfig.speed,
  });
  useEffect(() => {
    setBasket(prev => ({
      ...prev,
      y: getBasketY(),
    }));
  }, [deviceInfo.isDesktop, deviceInfo.isLandscape, basketConfig.maxY, basketConfig.heightPercent]);

  // Basket reference for performance
  const basketRef = useRef(basket);
  useEffect(() => {
    basketRef.current = basket;
  }, [basket]);

  // Enhanced input handling
  const [inputState, setInputState] = useState({
    dragging: false,
    touchActive: false,
    mouseActive: false,
    lastTouchTime: 0,
    lastMouseTime: 0,
  });

  const dragOffsetRef = useRef<number>(0);
  const lastUpdateTimeRef = useRef<number>(0);
  const inputThrottleRef = useRef<number>(deviceInfo.isMobile ? 16 : 32);

  // Game elements
  const [fallingItems, setFallingItems] = useState<FallingItem[]>([]);
  const [scoreEffects, setScoreEffects] = useState<ScoreEffect[]>([]);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [particles, setParticles] = useState<any[]>([]);
  
  // Game loop references
  const gameLoopRef = useRef<number>();
  const keysRef = useRef<Set<string>>(new Set());
  const lastFrameTimeRef = useRef<number>(0);
  
  // Performance monitoring
  const performanceRef = useRef({
    frameCount: 0,
    lastFpsCheck: 0,
    fps: 0,
    itemCount: 0,
    particleCount: 0,
  });

  // Particle ID generator
  const nextParticleId = useRef(1);

  // Enhanced internationalization
  const [language, setLanguage] = useState<'en' | 'vi'>(deviceInfo.isTablet ? 'vi' : 'en');

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Enhanced fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.log('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.log('Exit fullscreen error:', err);
      });
    }
  }, []);

  // Listen for fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Enhanced internationalization text
  const t = useMemo(() => ({
    en: {
      title: 'Catch Eggs Game',
      subtitle: 'Catch Eggs Game',
      start: 'Start Game',
      pause: 'Pause',
      resume: 'Resume',
      score: 'Score',
      level: 'Level',
      time: 'Time',
      combo: 'Combo',
      gameOver: 'Game Over',
      finalScore: 'Final Score',
      restart: 'Play Again',
      home: 'Home',
      rules: 'Game Rules',
      rule1: 'Eggs: +10 points',
      rule2: 'Diamonds: +30 points',
      rule3: 'Bombs: -30 points',
      rule4: 'Stars: +20 points',
      rule5: 'Combo: +3 bonus per 3 combos',
      rule6: 'Level up: items fall faster',
      tips: 'Drag basket to catch items! More combos = more rewards!',
      soundOn: 'Sound On',
      soundOff: 'Sound Off',
      newRecord: 'New Record!',
      maxCombo: 'Max Combo',
      reachedLevel: 'Reached Level',
      highScore: 'High Score',
      pauseTitle: 'Game Paused',
      pauseText: 'Click continue to resume',
      continue: 'Continue',
      controls: 'Controls',
      controlsHint: 'Use arrow keys or drag to move basket',
      achievement: {
        first100: '🎉 First 100 points!',
        score500: '🏆 500 points!',
        combo5: '🔥 5 combo!',
        combo10: '⚡ 10 combo!',
        level5: '⭐ Level 5!',
      }
    },
    vi: {
      title: 'Trò Chơi Bắt Trứng',
      subtitle: 'Trò Chơi Bắt Trứng',
      start: 'Bắt Đầu',
      pause: 'Tạm Dừng',
      resume: 'Tiếp Tục',
      score: 'Điểm',
      level: 'Cấp',
      time: 'Thời Gian',
      combo: 'Combo',
      gameOver: 'Kết Thúc',
      finalScore: 'Điểm Cuối',
      restart: 'Chơi Lại',
      home: 'Trang Chủ',
      rules: 'Luật Chơi',
      rule1: 'Trứng: +10 điểm',
      rule2: 'Kim cương: +30 điểm',
      rule3: 'Bom: -30 điểm',
      rule4: 'Sao: +20 điểm',
      rule5: 'Combo: +3 thưởng mỗi 3 combo',
      rule6: 'Tăng cấp: vật rơi nhanh hơn',
      tips: 'Kéo giỏ để bắt vật! Combo nhiều = thưởng nhiều!',
      soundOn: 'Bật Âm Thanh',
      soundOff: 'Tắt Âm Thanh',
      newRecord: 'Kỷ Lục Mới!',
      maxCombo: 'Combo Tối Đa',
      reachedLevel: 'Đạt Cấp',
      highScore: 'Điểm Cao Nhất',
      pauseTitle: 'Tạm Dừng',
      pauseText: 'Nhấp tiếp tục để chơi',
      continue: 'Tiếp Tục',
      controls: 'Điều Khiển',
      controlsHint: 'Dùng phím mũi tên hoặc kéo để di chuyển giỏ',
      achievement: {
        first100: '🎉 100 điểm đầu tiên!',
        score500: '🏆 500 điểm!',
        combo5: '🔥 5 combo!',
        combo10: '⚡ 10 combo!',
        level5: '⭐ Cấp 5!',
      }
    }
  }), []);

  // Enhanced item configuration with device-specific adjustments
  const getItemConfig = useCallback((level: number) => {
    const baseConfig = {
      egg: { emoji: '🥚', points: 10, color: '#FFD700', probability: 0.7 },
      diamond: { emoji: '💎', points: 30, color: '#00D4FF', probability: 0.15 },
      bomb: { emoji: '💣', points: -30, color: '#FF4757', probability: 0.1 },
      star: { emoji: '⭐', points: 20, color: '#FFA502', probability: 0.05 },
    };

    const levelFactor = Math.min(level / 10, 1);
    return {
      egg: { ...baseConfig.egg, probability: 0.7 - levelFactor * 0.2 },
      diamond: { ...baseConfig.diamond, probability: 0.15 + levelFactor * 0.1 },
      bomb: { ...baseConfig.bomb, probability: 0.1 + levelFactor * 0.15 },
      star: { ...baseConfig.star, probability: 0.05 + levelFactor * 0.05 },
    };
  }, []);

  // Enhanced falling item creation
  const createFallingItem = useCallback((): FallingItem => {
    const config = getItemConfig(gameState.level);
    const rand = Math.random();
    let type: 'egg' | 'diamond' | 'bomb' | 'star' = 'egg';
    let acc = 0;
    for (const key of ['egg', 'diamond', 'bomb', 'star'] as const) {
      acc += config[key].probability;
      if (rand < acc) {
        type = key;
        break;
      }
    }
    
    // Device-specific speed adjustments
    const baseSpeed = 0.8 + Math.random() * 0.4;
    const performanceSpeed = Math.min(gameState.itemsCaught / 15, 0.8);
    const levelSpeed = (gameState.level - 1) * 0.08;
    const deviceSpeedMultiplier = deviceInfo.isMobile ? 0.9 : deviceInfo.isTablet ? 1.1 : 1.0;
    
    const finalSpeed = Math.min(
      (baseSpeed + performanceSpeed + levelSpeed) * deviceSpeedMultiplier, 
      2.5
    );
    
    return {
      id: Date.now() + Math.random(),
      type,
      emoji: config[type].emoji,
      points: config[type].points,
      color: config[type].color,
      x: Math.random() * 90,
      y: -10,
      speed: finalSpeed,
    };
  }, [gameState.level, gameState.itemsCaught, getItemConfig, deviceInfo]);

  // Enhanced collision detection
  const checkCollision = useCallback((item: FallingItem, basketState: Basket): boolean => {
    let BASKET_WIDTH, BASKET_HEIGHT;
    if (deviceInfo.isDesktop) {
      // PC端判定盒与图标宽高一致
      BASKET_WIDTH = basketConfig.widthPercent;
      BASKET_HEIGHT = basketConfig.heightPercent;
    } else {
      // 移动端更宽松
      BASKET_WIDTH = basketConfig.widthPercent * 1.0;
      BASKET_HEIGHT = basketConfig.heightPercent;
    }
    const BASKET_LEFT = basketState.x - BASKET_WIDTH / 2;
    const BASKET_RIGHT = basketState.x + BASKET_WIDTH / 2;
    const BASKET_TOP = basketState.y - BASKET_HEIGHT / 2;
    const BASKET_BOTTOM = basketState.y + BASKET_HEIGHT / 2;
    const ITEM_SIZE = deviceInfo.isMobile ? 10 : 8;
    const itemCenterX = item.x + ITEM_SIZE / 2;
    const itemCenterY = item.y + ITEM_SIZE / 2;
    const horizontalCollision = itemCenterX >= BASKET_LEFT && itemCenterX <= BASKET_RIGHT;
    const verticalCollision = itemCenterY >= BASKET_TOP && itemCenterY <= BASKET_BOTTOM;
    return horizontalCollision && verticalCollision;
  }, [basketConfig, deviceInfo]);

  // Enhanced score effect
  const addScoreEffect = useCallback((x: number, y: number, points: number, type: 'positive' | 'negative' | 'bonus') => {
    const effect: ScoreEffect = {
      id: nextParticleId.current++,
      x: x + 4,
      y: y - 2,
      points,
      type,
      timestamp: Date.now(),
    };
    setScoreEffects(prev => {
      const combined = [...prev, effect];
      return combined.slice(-20);
    });
    
    const timeoutId = setTimeout(() => {
      setScoreEffects(prev => prev.filter(e => e.id !== effect.id));
    }, 1500);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Enhanced particle effect
  const addParticles = useCallback((x: number, y: number, count: number = 8) => {
    const newParticles = Array.from({ length: count }, () => ({
      id: nextParticleId.current++,
      x: x + Math.random() * 16 - 8,
      y: y + Math.random() * 16 - 8,
      vx: (Math.random() - 0.5) * 4,
      vy: (Math.random() - 0.5) * 4 - 2,
      life: 1.0,
    }));
    setParticles(prev => {
      const combined = [...prev, ...newParticles];
      return combined.slice(-50);
    });
  }, []);

  // Enhanced item catch handling
  const handleItemCatch = useCallback((item: FallingItem) => {
    setGameState(prev => {
      if (item.type === 'bomb') {
        addScoreEffect(item.x, item.y, -30, 'negative');
        addParticles(item.x, item.y, 6);
        return {
          ...prev,
          score: Math.max(0, prev.score - 30),
          combo: 0,
        };
      } else {
        const comboBonus = Math.floor(prev.combo / 3) * 3;
        const totalPoints = item.points + comboBonus;
        const newLevel = Math.floor((prev.score + totalPoints) / 80) + 1;
        addScoreEffect(
          item.x, 
          item.y, 
          totalPoints, 
          item.type === 'diamond' || item.type === 'star' ? 'bonus' : 'positive'
        );
        addParticles(item.x, item.y, 12);
        
        return {
          ...prev,
          score: prev.score + totalPoints,
          combo: prev.combo + 1,
          maxCombo: Math.max(prev.maxCombo, prev.combo + 1),
          level: newLevel,
          itemsCaught: prev.itemsCaught + 1,
        };
      }
    });
  }, [addScoreEffect, addParticles]);

  // Enhanced keyboard control
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      keysRef.current.add(e.key.toLowerCase());
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      e.preventDefault();
      keysRef.current.delete(e.key.toLowerCase());
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Enhanced basket update
  const updateBasket = useCallback(() => {
    if (!gameState.gameStarted || gameState.paused || inputState.dragging) return;
    setBasket(prev => {
      let newX = prev.x;
      let newY = prev.y;
      if (keysRef.current.has('arrowleft') || keysRef.current.has('a')) {
        newX -= prev.speed;
      }
      if (keysRef.current.has('arrowright') || keysRef.current.has('d')) {
        newX += prev.speed;
      }
      
      const basketSizeX = basketConfig.widthPercent;
      newX = Math.max(0, Math.min(100 - basketSizeX, newX));
      newY = Math.min(prev.y, basketConfig.maxY);
      return { ...prev, x: newX, y: newY };
    });
  }, [gameState.gameStarted, gameState.paused, inputState.dragging, basketConfig]);

  // Enhanced game loop
  const gameLoop = useCallback((timestamp: number) => {
    if (!gameState.gameStarted || gameState.paused) return;
    
    // Performance monitoring
    performanceRef.current.frameCount++;
    if (timestamp - performanceRef.current.lastFpsCheck >= 1000) {
      performanceRef.current.fps = performanceRef.current.frameCount;
      performanceRef.current.frameCount = 0;
      performanceRef.current.lastFpsCheck = timestamp;
    }
    
    // Dynamic frame rate control
    const targetFPS = deviceInfo.isMobile ? 45 : 60;
    const minFrameTime = 1000 / targetFPS;
    
    if (timestamp - lastFrameTimeRef.current < minFrameTime) {
      gameLoopRef.current = requestAnimationFrame(gameLoop);
      return;
    }
    lastFrameTimeRef.current = timestamp;
    
    try {
      updateBasket();
      
      // Dynamic spawn rate based on device and performance
      const baseSpawnRate = deviceInfo.isMobile ? 0.006 : deviceInfo.isTablet ? 0.008 : 0.007;
      const performanceBonus = Math.min(gameState.itemsCaught / 10, 0.01);
      const levelBonus = gameState.level * 0.002;
      const spawnRate = Math.min(baseSpawnRate + performanceBonus + levelBonus, 0.025);
      
      setFallingItems(prev => {
        let newItems = prev;
        let caughtItems: FallingItem[] = [];
        
        if (Math.random() < spawnRate && prev.length < 25) {
          newItems = [...prev, createFallingItem()];
        }
        
        const currentBasket = basketRef.current;
        const updated = newItems.map(item => ({ ...item, y: item.y + item.speed }));
        
        const nonCaughtItems: FallingItem[] = [];
        updated.forEach(item => {
          if (item.y >= 105) {
            return;
          }
          
          if (item.y > currentBasket.y + basketConfig.heightPercent + 5) {
            return;
          }
          
          if (checkCollision(item, currentBasket)) {
            caughtItems.push(item);
          } else {
            nonCaughtItems.push(item);
          }
        });
        
        performanceRef.current.itemCount = nonCaughtItems.length;
        
        if (caughtItems.length > 0) {
          setTimeout(() => {
            caughtItems.forEach(item => handleItemCatch(item));
          }, 0);
        }
        
        return nonCaughtItems;
      });
      
      setParticles(prev => {
        const updated = prev.map(particle => ({
          ...particle,
          x: particle.x + particle.vx,
          y: particle.y + particle.vy,
          life: particle.life - 0.02,
        }));
        const filtered = updated.filter(particle => particle.life > 0);
        performanceRef.current.particleCount = filtered.length;
        return filtered;
      });
      
    } catch (error) {
      console.error('Game loop error:', error);
    }
    
    gameLoopRef.current = requestAnimationFrame(gameLoop);
  }, [gameState.gameStarted, gameState.paused, updateBasket, createFallingItem, checkCollision, handleItemCatch, deviceInfo, basketConfig]);

  // Game loop control
  useEffect(() => {
    if (gameState.gameStarted && !gameState.paused) {
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    } else if (gameLoopRef.current) {
      cancelAnimationFrame(gameLoopRef.current);
    }

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameState.gameStarted, gameState.paused, gameLoop]);

  // Enhanced countdown
  useEffect(() => {
    if (!gameState.gameStarted || gameState.paused || gameState.timeLeft <= 0) return;

    const timer = setInterval(() => {
      setGameState(prev => {
        const newTimeLeft = prev.timeLeft - 1;
        if (newTimeLeft <= 0) {
          return {
            ...prev,
            timeLeft: 0,
            gameOver: true,
            gameStarted: false,
            paused: false,
          };
        }
        return { ...prev, timeLeft: newTimeLeft };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState.gameStarted, gameState.paused, gameState.timeLeft]);

  // Enhanced input handling
  const handleInputStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragOffsetRef.current = clientX - rect.left;
    
    setInputState(prev => ({
      ...prev,
      dragging: true,
      touchActive: 'touches' in e,
      mouseActive: !('touches' in e),
      lastTouchTime: 'touches' in e ? Date.now() : prev.lastTouchTime,
      lastMouseTime: !('touches' in e) ? Date.now() : prev.lastMouseTime,
    }));
    
    lastUpdateTimeRef.current = Date.now();
  }, []);

  const handleInputMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!inputState.dragging) return;
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const throttleTime = inputThrottleRef.current;
    if (now - lastUpdateTimeRef.current < throttleTime) {
      return;
    }
    lastUpdateTimeRef.current = now;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
    
    const newX = ((clientX - rect.left) / rect.width) * 100;
    const basketSizeX = basketConfig.widthPercent;
    const clampedX = Math.max(0, Math.min(100 - basketSizeX, newX));
    
    setBasket(prev => {
      const threshold = deviceInfo.isMobile ? 0.1 : 0.05;
      if (Math.abs(prev.x - clampedX) > threshold) {
        return { ...prev, x: clampedX };
      }
      return prev;
    });
  }, [inputState.dragging, deviceInfo, basketConfig]);

  const handleInputEnd = useCallback(() => {
    setInputState(prev => ({
      ...prev,
      dragging: false,
      touchActive: false,
      mouseActive: false,
    }));
  }, []);

  // Enhanced mouse event handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleInputStart(e);
  }, [handleInputStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (inputState.dragging && inputState.mouseActive) {
      handleInputMove(e);
    }
  }, [inputState.dragging, inputState.mouseActive, handleInputMove]);

  const handleMouseUp = useCallback(() => {
    if (inputState.mouseActive) {
      handleInputEnd();
    }
  }, [inputState.mouseActive, handleInputEnd]);

  // Enhanced touch event handling
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (deviceInfo.isTablet && e.touches.length > 1) {
      e.preventDefault();
      return;
    }
    handleInputStart(e);
  }, [handleInputStart, deviceInfo]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (inputState.dragging && inputState.touchActive) {
      if (deviceInfo.isTablet && e.touches.length > 1) {
        e.preventDefault();
        return;
      }
      handleInputMove(e);
    }
  }, [inputState.dragging, inputState.touchActive, handleInputMove, deviceInfo]);

  const handleTouchEnd = useCallback(() => {
    if (inputState.touchActive) {
      handleInputEnd();
    }
  }, [inputState.touchActive, handleInputEnd]);

  // Enhanced global mouse event listener
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (inputState.dragging && inputState.mouseActive) {
        const now = Date.now();
        const throttleTime = inputThrottleRef.current;
        if (now - lastUpdateTimeRef.current < throttleTime) {
          return;
        }
        lastUpdateTimeRef.current = now;
        
        const rect = document.querySelector('.game-canvas')?.getBoundingClientRect();
        if (rect) {
          const newX = ((e.clientX - rect.left) / rect.width) * 100;
          const basketSizeX = basketConfig.widthPercent;
          const clampedX = Math.max(0, Math.min(100 - basketSizeX, newX));
          
          setBasket(prev => {
            const threshold = deviceInfo.isMobile ? 0.1 : 0.05;
            if (Math.abs(prev.x - clampedX) > threshold) {
              return { ...prev, x: clampedX };
            }
            return prev;
          });
        }
      }
    };

    const handleGlobalMouseUp = () => {
      if (inputState.mouseActive) {
        setInputState(prev => ({
          ...prev,
          dragging: false,
          mouseActive: false,
        }));
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [inputState.dragging, inputState.mouseActive, deviceInfo, basketConfig]);

  // Enhanced game start
  const startGame = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      score: 0,
      level: 1,
      timeLeft: 180,
      gameStarted: true,
      gameOver: false,
      paused: false,
      combo: 0,
      maxCombo: 0,
      itemsCaught: 0,
    }));
    setFallingItems([]);
    setScoreEffects([]);
    setParticles([]);
    setShowStartScreen(false);
  }, []);

  // Enhanced pause/resume
  const togglePause = useCallback(() => {
    setGameState(prev => ({ ...prev, paused: !prev.paused }));
  }, []);

  // Enhanced language toggle
  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'vi' : 'en');
  }, []);

  // Enhanced time formatting
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Achievement popup state
  const [showAchievement] = useState<string | null>(null);

  // 1. 新增ref和state
  const basketSpriteRef = useRef<HTMLDivElement>(null);

  // 2. 监听图标渲染后获取实际宽高
  useEffect(() => {
    if (deviceInfo.isDesktop && basketSpriteRef.current) {
      // const rect = basketSpriteRef.current.getBoundingClientRect();
      // setSpriteRect({ width: rect.width, height: rect.height }); // This line was removed
    }
  }, [deviceInfo.isDesktop, basket.x, basket.y, basketConfig.widthPercent, basketConfig.heightPercent]);

  return (
    <div className="game-app">
      {/* Enhanced start screen */}
      {showStartScreen && !gameState.gameStarted && (
        <div className="start-screen">
          <div className="start-content">
            <div className="game-logo">
              <div className="logo-icon">🧺</div>
              <h1 className="logo-title">{t[language].title}</h1>
              <p className="logo-subtitle">{t[language].subtitle}</p>
            </div>
            
            <div className="game-rules">
              <h3>{t[language].rules}</h3>
              <div className="rules-grid">
                <div className="rule-item good">
                  <span className="rule-icon">🥚</span>
                  <span className="rule-text">{t[language].rule1}</span>
                </div>
                <div className="rule-item special">
                  <span className="rule-icon">💎</span>
                  <span className="rule-text">{t[language].rule2}</span>
                </div>
                <div className="rule-item special">
                  <span className="rule-icon">⭐</span>
                  <span className="rule-text">{t[language].rule4}</span>
                </div>
                <div className="rule-item bad">
                  <span className="rule-icon">💣</span>
                  <span className="rule-text">{t[language].rule3}</span>
                </div>
                <div className="rule-item good">
                  <span className="rule-icon">🔥</span>
                  <span className="rule-text">{t[language].rule5}</span>
                </div>
                <div className="rule-item good">
                  <span className="rule-icon">📈</span>
                  <span className="rule-text">{t[language].rule6}</span>
                </div>
              </div>
            </div>

            <button className="start-button" onClick={startGame}>
              <span className="button-content">
                <span className="button-icon">🎮</span>
                <span className="button-text">{t[language].start}</span>
              </span>
            </button>

            <div className="controls-hint">
              <p>{t[language].tips}</p>
            </div>

            {/* Enhanced language toggle */}
            <button 
              className="language-toggle" 
              onClick={toggleLanguage}
            >
              {language === 'en' ? '🇻🇳' : '🇺🇸'}
            </button>
          </div>
        </div>
      )}

      {/* Enhanced game screen */}
      {(gameState.gameStarted || gameState.gameOver) && (
        <div className="game-screen">
          {/* Enhanced header status bar */}
          <header className="game-header">
            <div className="header-row">
              <div className="stats-container">
                <div className="stat-card score-card">
                  <div className="stat-value">{gameState.score.toLocaleString()}</div>
                  <div className="stat-label">{t[language].score}</div>
                </div>
                <div className="stat-card time-card">
                  <div className={`stat-value ${gameState.timeLeft <= 10 ? 'warning' : ''}`}>{formatTime(gameState.timeLeft)}</div>
                  <div className="stat-label">{t[language].time}</div>
                </div>
                <div className="stat-card combo-card">
                  <div className="stat-value">{gameState.combo}</div>
                  <div className="stat-label">{t[language].combo}</div>
                </div>
                <div className="stat-card level-card">
                  <div className="stat-value">{gameState.level}</div>
                  <div className="stat-label">{t[language].level}</div>
                </div>
              </div>
              <div className="header-actions">
                {(deviceInfo.isTablet || deviceInfo.isDesktop) && (
                  <button 
                    className="control-btn fullscreen-btn" 
                    onClick={toggleFullscreen}
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  >
                    {isFullscreen ? '⏹️' : '⛶'}
                  </button>
                )}
                <button 
                  className="control-btn pause-btn" 
                  onClick={togglePause}
                  disabled={gameState.gameOver}
                  title={gameState.paused ? t[language].resume : t[language].pause}
                >
                  {gameState.paused ? '▶️' : '⏸️'}
                </button>
              </div>
            </div>
            {/* Enhanced debug panel */}
            {window.location.hostname === 'localhost' && (
              <div className="debug-panel">
                <div className="debug-item">FPS: {performanceRef.current.fps}</div>
                <div className="debug-item">Items: {performanceRef.current.itemCount}</div>
                <div className="debug-item">Particles: {performanceRef.current.particleCount}</div>
                <div className="debug-item">Level: {gameState.level}</div>
                <div className="debug-item">Caught: {gameState.itemsCaught}</div>
                <div className="debug-item">Device: {deviceInfo.isMobile ? 'Mobile' : deviceInfo.isTablet ? 'Tablet' : 'Desktop'}</div>
                <div className="debug-item">Orientation: {deviceInfo.isLandscape ? 'Landscape' : 'Portrait'}</div>
                <div className="debug-item">Basket: {basket.x.toFixed(1)}%, {basket.y.toFixed(1)}%</div>
                <div className="debug-item">Basket Size: {basketConfig.widthPercent}% x {basketConfig.heightPercent.toFixed(1)}%</div>
                <div className="debug-item">Canvas Height: {canvasHeight}px</div>
                <div className="debug-item">Screen: {window.innerWidth}x{window.innerHeight}</div>
                <button 
                  onClick={() => {
                    console.log('Force basket visibility');
                    setBasket(prev => ({ ...prev, x: 50, y: 80 }));
                  }}
                  style={{
                    background: '#ff4444',
                    color: 'white',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    cursor: 'pointer'
                  }}
                >
                  Force Basket
                </button>
              </div>
            )}
          </header>

          {/* Enhanced game main area */}
          <main className="game-main">
            <div 
              className="game-canvas"
              style={{ 
                width: '100vw', 
                height: deviceInfo.isLandscape ? 'calc(100vh - 60px)' : 'calc(100vh - 120px)',
                maxWidth: '100vw', 
                maxHeight: '100vh', 
                borderRadius: 0 
              }}
            >
              {/* Enhanced background decorations */}
              <div className="background-decorations">
                <div className="floating-cloud cloud-1">☁️</div>
                <div className="floating-cloud cloud-2">☁️</div>
                <div className="floating-cloud cloud-3">☁️</div>
                <div className="sun-decoration">☀️</div>
              </div>

              {/* Enhanced basket */}
              <div
                className={`basket-element ${deviceInfo.isTablet ? 'tablet-mode' : ''} ${deviceInfo.isMobile ? 'mobile-mode' : ''}`}
                style={{
                  left: `${basket.x}%`,
                  top: `${basket.y}%`,
                  width: `${basketConfig.widthPercent}%`,
                  height: `${basketConfig.heightPercent}%`,
                  transform: 'translate(-50%, -50%)', // 以中心点定位
                  touchAction: 'none',
                  cursor: inputState.dragging ? 'grabbing' : 'grab',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  borderRadius: '8px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  zIndex: 1000,
                  ...(deviceInfo.isTablet && {
                    touchAction: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    WebkitTouchCallout: 'none',
                  }),
                  ...(deviceInfo.isMobile && {
                    touchAction: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    WebkitTouchCallout: 'none',
                  })
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDragStart={(e) => e.preventDefault()}
              >
                {/* 移除debug判定框，仅保留图标 */}
                <div className="basket-sprite" ref={basketSpriteRef}>🧺</div>
              </div>

              {/* Enhanced falling items */}
              {fallingItems.map((item) => (
                <div
                  key={item.id}
                  className={`item-element ${item.type}`}
                  style={{
                    left: `${item.x}%`,
                    top: `${item.y}%`,
                    width: `10%`,
                    height: `10%`,
                  }}
                >
                  <div className="item-sprite">{item.emoji}</div>
                  <div className="item-glow"></div>
                </div>
              ))}

              {/* Enhanced score effects */}
              {scoreEffects.map((effect) => (
                <div
                  key={effect.id}
                  className={`score-popup ${effect.type}`}
                  style={{
                    left: `${effect.x}%`,
                    top: `${effect.y}%`,
                  }}
                >
                  {effect.type === 'negative' ? `${effect.points}` : `+${effect.points}`}
                </div>
              ))}

              {/* Enhanced particle effects */}
              {particles.map((particle) => (
                <div
                  key={particle.id}
                  className="particle"
                  style={{
                    left: `${particle.x}%`,
                    top: `${particle.y}%`,
                    opacity: particle.life,
                    transform: `scale(${particle.life})`,
                  }}
                >
                  ✨
                </div>
              ))}

              {/* Enhanced progress bar */}
              <div className="progress-bg">
                <div 
                  className="progress-fill"
                  style={{ width: `${(gameState.timeLeft / 60) * 100}%` }}
                ></div>
              </div>
            </div>
          </main>

          {/* Enhanced bottom tips */}
          <footer className="game-footer">
            <div className="tips-container">
              <p className="tip-text">{t[language].tips}</p>
            </div>
          </footer>
        </div>
      )}

      {/* Enhanced achievement hint */}
      {showAchievement && (
        <div className="achievement-popup">
          <div className="achievement-content">
            <span className="achievement-icon">🏆</span>
            <span className="achievement-text">{showAchievement}</span>
          </div>
        </div>
      )}

      {/* Enhanced pause overlay */}
      {gameState.paused && (
        <div className="pause-overlay">
          <div className="pause-card">
            <h2>⏸️ {t[language].pauseTitle}</h2>
            <p>{t[language].pauseText}</p>
            <button className="resume-btn" onClick={togglePause}>
              {t[language].continue}
            </button>
          </div>
        </div>
      )}

      {/* Enhanced game over */}
      {gameState.gameOver && (
        <div className="game-over-overlay">
          <div className="game-over-card">
            <div className="result-header">
              <h2>🎉 {t[language].gameOver}</h2>
            </div>

            <div className="final-score">
              <div className="score-display">
                <span className="score-number">{gameState.score.toLocaleString()}</span>
                <span className="score-label">{t[language].finalScore}</span>
              </div>
            </div>

            <div className="stats-summary">
              <div className="summary-item">
                <span className="summary-value">{gameState.maxCombo}</span>
                <span className="summary-label">{t[language].maxCombo}</span>
              </div>
              <div className="summary-item">
                <span className="summary-value">{gameState.level}</span>
                <span className="summary-label">{t[language].reachedLevel}</span>
              </div>
            </div>

            <div className="restart-actions">
              <button className="restart-btn" onClick={startGame}>
                <span className="button-content">
                  <span className="button-icon">🔄</span>
                  <span className="button-text">{t[language].restart}</span>
                </span>
              </button>
              <button 
                className="home-btn" 
                onClick={() => {
                  setShowStartScreen(true);
                  setGameState(prev => ({ ...prev, gameOver: false }));
                }}
              >
                <span className="button-content">
                  <span className="button-icon">🏠</span>
                  <span className="button-text">{t[language].home}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PickGame; 